<template>
  <Dropdown
    v-if="shouldDisplayRef"
    class-name="session-mentee-report"
    data-test-id="session-mentee-report-section"
    :title="t('session.menteeReport.title')"
    :default-open="true"
    :is-loaded="!isPendingRef"
  >
    <div v-if="sessionRef?.menteeReports && sessionRef.menteeReports.length > 0">
      <div
        v-for="report in sessionRef.menteeReports"
        :key="report.userId"
        class="session-mentee-report__item"
      >
        <p class="session-mentee-report__author">
          {{ t('session.menteeReport.author') }} : {{ getMenteeName(report) }}
        </p>
        <p
          v-if="report.createdAt < report.updatedAt"
          class="session-mentee-report__report"
          data-test-id="session-mentee-report-content"
        >
          {{ report.comment }} <br />
          <span class="session-mentee-report__report__updated-at"
            >{{ t('session.menteeReport.modified') }}
            {{ dayjsClient(report.updatedAt).format('DD/MM/YYYY - HH:mm') }}</span
          >
        </p>
        <p v-else class="session-mentee-report__report" data-test-id="session-mentee-report-content">
          "{{ report.comment }}"
        </p>
        <div v-if="canEditReport(report)" class="c-btns-container c-btn-center">
          <CustomButton
            usage="button"
            color="primary"
            data-test-id="mentee-report-edit-button"
            :text="t('session.menteeReportModal.modifyReport')"
            @emit:click="onClickModify"
          >
            <template #icon>
              <EditIcon color="white" />
            </template>
          </CustomButton>
        </div>
      </div>
    </div>
    <EmptyContent v-else :text="t('session.menteeReport.empty')" />
  </Dropdown>
</template>

<script setup lang="ts">
import { computed } from 'vue'

import useAccount from '@/features/auth/hooks/use-account.hook'
import { RoleEnumTypeV2 } from '@/features/auth/types'
import useProgram from '@/features/programs/hooks/use-program.hook'
import { selectGetSelectedProgramId } from '@/features/programs/stores/programs/programs.selectors'
import programsStore from '@/features/programs/stores/programs/programs.store'
import useSession from '@/features/sessions/hooks/use-session.hook'
import CustomButton from '@/features/ui/components/button/custom-button.vue'
import Dropdown from '@/features/ui/components/dropdown/dropdown.vue'
import EmptyContent from '@/features/ui/components/empty-content/empty-content.vue'
import EditIcon from '@/features/ui/components/icons/edit-icon.vue'
import { selectGetSessionDetailsModal } from '@/features/ui/store/modal/modal.selectors'
import type { ReviewSessionModalType } from '@/features/ui/store/modal/modal.store'
import modalsStore from '@/features/ui/store/modal/modal.store'
import useGroupRoles from '@/features/users/hooks/use-group-roles.hook'
import dayjsClient from '@/lib/dayjs'
import { useI18n } from '@/lib/i18n'

const { t } = useI18n()

const sessionDetailsModalRef = selectGetSessionDetailsModal(modalsStore)
const sessionIdRef = computed(() => sessionDetailsModalRef.value.attachedData.sessionId ?? null)
const { sessionRef, isPendingRef } = useSession(sessionIdRef)

const programIdRef = selectGetSelectedProgramId(programsStore)
const { programRef } = useProgram(programIdRef)

const groupIdRef = computed(() => sessionRef.value?.mentoringGroupId ?? null)
const { groupRolesRef } = useGroupRoles({ groupIdRef })

const { accountRef } = useAccount()

// Only display if the setting is enabled
const shouldDisplayRef = computed(() => programRef.value?.settings.hasMenteeReviewSessionReport)

// Get mentee name from report
const getMenteeName = (report: any) => {
  if (!report?.user) return ''
  const user = report.user
  if (user.identity?.firstname && user.identity?.lastname) {
    return `${user.identity.firstname} ${user.identity.lastname}`
  }
  return user.email || ''
}

// Can edit if: current user is the author of this specific report
const canEditReport = (report: any) => {
  const isAuthor = report.userId === accountRef.value?.id
  return isAuthor && groupRolesRef.value?.includes(RoleEnumTypeV2.MENTEE)
}

const onClickModify = () => {
  modalsStore.toggleModal('review-session', true)
  modalsStore.toggleModal('session-details', false)

  modalsStore.setModalStep<ReviewSessionModalType>('review-session', 'mentee-report')
  modalsStore.updateModalAttachedData<ReviewSessionModalType>('review-session', {
    sessionId: sessionIdRef.value!,
  })
}
</script>

<style lang="scss" scoped>
@import '@/styles/utils';

.session-mentee-report {
  &__item {
    margin-bottom: 1.5rem;
    padding-bottom: 1.5rem;
    border-bottom: 1px solid #e0e0e0;

    &:last-child {
      border-bottom: none;
      margin-bottom: 0;
      padding-bottom: 0;
    }
  }

  &__author {
    font-size: 0.9rem;
    color: #666;
    margin-bottom: 0.5rem;
    font-weight: 600;
  }

  &__report {
    white-space: pre-wrap;
    word-break: break-word;

    &__updated-at {
      font-size: 0.8rem;
      color: rgb($text-muted);
      font-style: italic;
    }
  }
}
</style>
